import { useCallback, useId, useRef, useState, type KeyboardEvent } from 'react';
import type { ILookupProps } from './ILookupProps';
import { nextEnabledIndex } from './nextEnabledIndex';
import { useFloating } from './useFloating';

/**
 * Стан комбобокса (прототип fieldVM user): фокус відкриває список із порожнім запитом, введення шукає з першого символу,
 * ↑ ↓ рухають виділення, Enter вибирає виділений пункт або перший збіг (не «Не призначено»), Esc і втрата фокуса закривають.
 */
export function useLookup({ options, onQuery, onPick, readOnly, disabled }: ILookupProps) {
  const [query, setQuery] = useState<string | null>(null);
  const [hi, setHi] = useState(-1);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const listId = useId();
  const open = query !== null;
  const close = useCallback(() => { setQuery(null); setHi(-1); }, []);
  const pos = useFloating(open, box, panel, close, { estimate: Math.min(300, options.length * 44 + 40), minWidth: 240 });
  const start = () => { if (readOnly || disabled || open) return; setQuery(''); setHi(-1); onQuery(''); };
  const type = (q: string) => { setQuery(q); setHi(-1); onQuery(q); };
  const pick = (value: string) => { onPick(value); close(); };
  const onKeyDown = (e: KeyboardEvent) => {
    if (!open) { if (e.key === 'ArrowDown') { e.preventDefault(); start(); } return; }
    const disabledAt = options.map((o) => Boolean(o.disabled));
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => nextEnabledIndex(disabledAt, h, e.key === 'ArrowDown' ? 1 : -1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = hi >= 0 ? options[hi] : options.find((o) => o.value !== '' && !o.disabled);
      if (chosen && !chosen.disabled) pick(chosen.value);
    } else if (e.key === 'Tab') close();
  };
  const onBlur = () => setTimeout(() => {
    const a = document.activeElement;
    if (a && (box.current?.contains(a) || panel.current?.contains(a))) return;
    close();
  }, 180);
  return { open, query, hi, setHi, box, input, panel, listId, pos, start, type, pick, close, onKeyDown, onBlur };
}
